import { AfterViewInit, Component, ElementRef, Input, ViewChild } from "@angular/core";
import { AddCardToDeckModalComponent, DeckCardCountTotal } from "./add-card-to-deck-modal.component";
import { NgClass } from "@angular/common";
import { DeckLayerName, PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DeckCardAddition, DeckCardAdditionResult, DeckCardChanges, UpdateDeckRequest, UpdateDeckResponse } from "@apiserver-rest/ponder-data-client-base";
import { take } from "rxjs";
import { getDeckRoute } from "./deck-router";
import { styleDeckElementBackground } from "./deck-utils";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { EventBroker } from "@ponder-core/events/eventBroker";

@Component({
    selector: "add-card-to-deck-input",
    imports: [NgClass],
    templateUrl: "./add-card-to-deck-input.component.html"
})
export class AddCardToDeckInputComponent implements AfterViewInit {
    @Input({ required: true }) public parentRef!: AddCardToDeckModalComponent;
    @Input({ required: true }) public deckTotal!: DeckCardCountTotal;
    @Input({ required: true }) public itemId!: string;
    @Input({ required: true }) public hasNormal!: boolean;
    @Input({ required: true }) public hasSpecial!: boolean;

    @ViewChild("titleOuter") titleOuterRef!: ElementRef<HTMLElement>;
    @ViewChild("titleInner") titleInnerRef!: ElementRef<HTMLElement>;
    
    public readonly starSvgIcon: string = SvgIconMap.get(SvgIcon.Star)!;
    public readonly inDeckSvgIcon: string = SvgIconMap.get(SvgIcon.CheckCircle)!;
    
    public isUpdating: boolean = false;
    public deckRoute!: string;

    public constructor(private events: EventBroker, private ponderDataClient: PonderDataClient) {

    }

    public ngOnInit(): void {
        this.deckRoute = getDeckRoute(this.parentRef.username!, this.deckTotal.deck.deckId);
    }

    public ngAfterViewInit(): void {
        styleDeckElementBackground(
            this.titleOuterRef.nativeElement,
            this.titleInnerRef.nativeElement,
            this.deckTotal.deck.deckColor!,
            this.deckTotal.deck.backgroundCardId
        );
    }


    public adjustCount(main: number, side: number, maybe: number, mainFoil: number, sideFoil: number, maybeFoil: number): void {
        var newMain = Math.max(0, Math.min(99, this.deckTotal.deck.mainboardCount! + main));
        var newMainFoil = Math.max(0, Math.min(99, this.deckTotal.deck.mainboardSpecialCount! + mainFoil));
        var newSide = Math.max(0, Math.min(99, this.deckTotal.deck.sideboardCount! + side));
        var newSideFoil = Math.max(0, Math.min(99, this.deckTotal.deck.sideboardSpecialCount! + sideFoil));
        var newMaybe = Math.max(0, Math.min(99, this.deckTotal.deck.maybeboardCount! + maybe));
        var newMaybeFoil = Math.max(0, Math.min(99, this.deckTotal.deck.maybeboardSpecialCount! + maybeFoil));

        if ((newMain == this.deckTotal.deck.mainboardCount) && (newMainFoil == this.deckTotal.deck.mainboardSpecialCount) &&
            (newSide == this.deckTotal.deck.sideboardCount) && (newSideFoil == this.deckTotal.deck.sideboardSpecialCount) &&
            (newMaybe == this.deckTotal.deck.maybeboardCount) && (newMaybeFoil == this.deckTotal.deck.mainboardSpecialCount)) {
            return;
        }

        if (this.isUpdating) {
            return;
        }

        this.isUpdating = true;

        var prevMain = this.deckTotal.deck.mainboardCount ?? 0;
        var prevMainFoil = this.deckTotal.deck.mainboardSpecialCount ?? 0;
        var prevSide = this.deckTotal.deck.sideboardCount ?? 0;
        var prevSideFoil = this.deckTotal.deck.sideboardSpecialCount ?? 0;
        var prevMaybe = this.deckTotal.deck.maybeboardCount ?? 0;
        var prevMaybeFoil = this.deckTotal.deck.maybeboardSpecialCount ?? 0;

        var updatingMainboardCount = (prevMain != newMain) || (prevMainFoil != newMainFoil);
        var updatingSideboardCount = (prevSide != newSide) || (prevSideFoil != newSideFoil);
        var updatingMaybeboardCount = (prevMaybe != newMaybe) || (prevMaybeFoil != newMaybeFoil);

        this.setCounts(newMain, newMainFoil, newSide, newSideFoil, newMaybe, newMaybeFoil);

        var request = new UpdateDeckRequest({
            deckId: this.deckTotal.deck.deckId,
            cardAdditions: [],
            cardChanges: []
        });

        if (prevMain || prevMainFoil) {
            // Card already was on mainboard, adjusting count.
            request.cardChanges!.push(new DeckCardChanges({
                cardId: this.deckTotal.deck.mainboardDeckCardId!,
                count: newMain,
                specialCount: newMainFoil,
                layer: DeckLayerName.Mainboard
            }));
        } else {
            // Card is new to the mainboard
            request.cardAdditions!.push(new DeckCardAddition({
                cardId: this.itemId,
                count: newMain,
                specialCount: newMainFoil,
                layer: DeckLayerName.Mainboard
            }));
        }

        if (prevSide || prevSideFoil) {
            // Card already was on sideboard, adjusting count.
            request.cardChanges!.push(new DeckCardChanges({
                cardId: this.deckTotal.deck.sideboardDeckCardId!,
                count: newSide,
                specialCount: newSideFoil,
                layer: DeckLayerName.Sideboard
            }));
        } else {
            // Card is new to the sideboard
            request.cardAdditions!.push(new DeckCardAddition({
                cardId: this.itemId,
                count: newSide,
                specialCount: newSideFoil,
                layer: DeckLayerName.Sideboard
            }));
        }

        if (prevMaybe || prevMaybeFoil) {
            // Card already was on maybeboard, adjusting count.
            request.cardChanges!.push(new DeckCardChanges({
                cardId: this.deckTotal.deck.maybeboardDeckCardId!,
                count: newMaybe,
                specialCount: newMaybeFoil,
                layer: DeckLayerName.Maybeboard
            }));
        } else {
            // Card is new to the maybeboard
            request.cardAdditions!.push(new DeckCardAddition({
                cardId: this.itemId,
                count: newMaybe,
                specialCount: newMaybeFoil,
                layer: DeckLayerName.Maybeboard
            }));
        }

        this.ponderDataClient
            .deckUpdate(request)
            .pipe(take(1))
            .subscribe({
                next: (response: UpdateDeckResponse) => {
                    if (response.success) {
                        this.recordNewDeckCardIds(response.cardAdditions);
                        this.removeOldDeckCardIds();
                        
                        if (updatingMainboardCount) { this.emitUpdatedEvent(DeckLayerName.Mainboard, this.deckTotal.deck.mainboardCount, this.deckTotal.deck.mainboardSpecialCount); }
                        if (updatingSideboardCount) { this.emitUpdatedEvent(DeckLayerName.Sideboard, this.deckTotal.deck.sideboardCount, this.deckTotal.deck.sideboardSpecialCount); }
                        if (updatingMaybeboardCount) { this.emitUpdatedEvent(DeckLayerName.Maybeboard, this.deckTotal.deck.maybeboardCount, this.deckTotal.deck.maybeboardSpecialCount); }
                    } else {
                        this.setCounts(prevMain, prevMainFoil, prevSide, prevSideFoil, prevMaybe, prevMaybeFoil);
                    }
                },
                error: () => {
                    this.setCounts(prevMain, prevMainFoil, prevSide, prevSideFoil, prevMaybe, prevMaybeFoil);
                }
            })
            .add(() => { this.isUpdating = false; });
    }

    private setCounts(newMain: number, newMainFoil: number, newSide: number, newSideFoil: number, newMaybe: number, newMaybeFoil: number): void {
        this.deckTotal.deck.mainboardCount = newMain;
        this.deckTotal.deck.mainboardSpecialCount = newMainFoil;
        this.deckTotal.deck.sideboardCount = newSide;
        this.deckTotal.deck.sideboardSpecialCount = newSideFoil;
        this.deckTotal.deck.maybeboardCount = newMaybe;
        this.deckTotal.deck.maybeboardSpecialCount = newMaybeFoil;

        this.deckTotal.total = newMain + newMainFoil + newSide + newSideFoil + newMaybe + newMaybeFoil;
    }

    private recordNewDeckCardIds(additions: DeckCardAdditionResult[] | undefined): void {
        additions?.forEach(a => {
            switch (a.layer) {
                case DeckLayerName.Mainboard:
                    this.deckTotal.deck.mainboardDeckCardId = a.deckCard.deckCardId;
                    break;

                case DeckLayerName.Sideboard:
                    this.deckTotal.deck.sideboardDeckCardId = a.deckCard.deckCardId;
                    break;

                case DeckLayerName.Planning:
                    this.deckTotal.deck.maybeboardDeckCardId = a.deckCard.deckCardId;
                    break;

                default:
                    break;
            }
        });
    }

    private removeOldDeckCardIds(): void {
        if (!this.deckTotal.deck.mainboardCount && !this.deckTotal.deck.mainboardSpecialCount) {
            this.deckTotal.deck.mainboardDeckCardId = undefined;
        }

        if (!this.deckTotal.deck.sideboardCount && !this.deckTotal.deck.sideboardSpecialCount) {
            this.deckTotal.deck.sideboardDeckCardId = undefined;
        }

        if (!this.deckTotal.deck.maybeboardCount && !this.deckTotal.deck.maybeboardSpecialCount) {
            this.deckTotal.deck.maybeboardDeckCardId = undefined;
        }
    }

    private emitUpdatedEvent(layer: string, count: number | undefined, specialCount: number | undefined): void {
        this.events.deck.indirectDeckCardUpdatedEvent.emit({
            deckId: this.deckTotal.deck.deckId,
            deckName: this.deckTotal.deck.deckName!,
            deckColor: this.deckTotal.deck.deckColor!,
            backgroundCardId: this.deckTotal.deck.backgroundCardId,
            layer: layer,
            itemId: this.itemId,
            count: count ?? 0,
            specialCount: specialCount ?? 0
        });
    }
}